import { applyExperienceV2 } from './scenario-upgrade.js';
import { SceneIntro } from './intro.js';
import { ExperienceApi } from './api.js';
import { ExperienceAudio } from './audio.js';
import { ExperienceEngine, ViewModes } from './engine.js';
import { playSound } from './sfx.js';

const byId = id => document.getElementById(id);
const safe = value => String(value ?? '').replace(/[&<>'"]/g, character => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
})[character]);

export class PeaceLabsGame {
  constructor() {
    const query = new URLSearchParams(location.search);
    this.programSlug = query.get('program') || 'baton-rouge-peace';
    this.scenarioSlug = query.get('scenario') || 'corner-store-pause';
    this.previewToken = query.get('preview') || '';
    // which version of the scene (?as=you / friend); blank = the player picks before the scene loads
    this.versionKey = (query.get('as') || '').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 40);
    this.api = new ExperienceApi(window.PEACE_BOOT?.csrf || '');
    this.audio = new ExperienceAudio(label => this.setAudioStatus(label));
    this.engine = null;
    this.boot = null;
    this.definition = null;
    this.nodeKey = '';
    this.nodeToken = 0;
    this.started = false;
    this.pressure = 0;
    this.currentChoices = [];
    this.sessionMode = ViewModes.BROWSER;
    this.voiceCache = new Map();
    this.skillHistory = new Set();
    this.mediaToken = 0;
    // live scenes (definition.live): the scene is played turn by turn from the server's scene state
    this.liveMode = false;
    this.isTest = false;
    this.lastTurnId = 0;
    this.liveVoices = [];
    this.voice2 = new ExperienceAudio(() => {});
  }

  async initialize() {
    this.bindUi();
    this.setLoading('Loading the published scenario…');
    this.boot = await this.api.bootstrap(this.programSlug, this.scenarioSlug, this.previewToken, this.versionKey);
    // a scene that can be played more than one way: the player picks first, then the scene loads as that version
    const versions = Array.isArray(this.boot.scenario.definition?.versions) ? this.boot.scenario.definition.versions : [];
    if (versions.length > 1 && !this.versionKey) {
      this.versionKey = await this.chooseVersion(versions, this.boot.scenario);
      this.setLoading('Loading the scene…');
      this.boot = await this.api.bootstrap(this.programSlug, this.scenarioSlug, this.previewToken, this.versionKey);
    }
    this.versionKey = String(this.boot.scenario.definition?.versionkey || this.versionKey || '');
    this.definition = applyExperienceV2(this.boot.scenario.definition);
    this.liveMode = Boolean(this.definition.live && this.definition.live.enabled);
    if (this.boot.preview) this.showTestBadge();
    this.censorWeapons = Boolean(this.definition.live && this.definition.live.censorweapons);
    this.engine = new ExperienceEngine(byId('gameCanvas'), {
      choose: choice => this.choose(choice),
      sensorDenied: () => this.notice('Motion permission was not granted. Touch-drag viewing remains available.'),
      xrUnavailable: () => this.notice('Immersive VR is not available in this browser. Browser play remains available.'),
      xrEnded: () => {
        this.sessionMode = ViewModes.BROWSER;
        document.body.dataset.viewMode = this.sessionMode;
        this.notice('You left VR. The scene continues here in the browser.');
      },
      assetError: (type, url) => this.assetError(type, url)
    });
    await this.engine.initialize(this.definition);
    if (this.censorWeapons) this.engine.staged?.setCensor(true);
    this.engine.app.on('update', () => {
      if (this.liveVoices.length) {
        // live turns: every voice that is playing moves its own speaker's mouth
        const levels = {};
        this.liveVoices.forEach(voice => { if (voice.key && !voice.audio.element.paused) levels[voice.key] = voice.audio.mouthLevel(); });
        this.engine.setSpeakingLevels(levels);
      } else this.engine.setMouthLevel(this.audio.mouthLevel());
    });
    this.fillPublishedContent();
    this.setLoading('');
    byId('loading').hidden = true;
    byId('experience').hidden = false;
    requestAnimationFrame(() => this.engine.app.resizeCanvas());
    byId('briefing').classList.add('open');
    this.reportCastState();
    // optional spoken + shown briefing; the written briefing stays as it is (and is where the intro always ends)
    try {
      this.intro = new SceneIntro({ engine: this.engine, definition: this.definition, api: this.api, program: this.programSlug, scenario: this.scenarioSlug, as: this.versionKey });
      this.intro.mount();
    } catch { /* the written briefing is enough */ }
  }

  bindUi() {
    document.querySelectorAll('[data-mode]').forEach(button => {
      button.addEventListener('click', () => this.start(button.dataset.mode));
    });
    byId('recenterBtn').addEventListener('click', () => this.engine?.recenter());
    byId('soundBtn').addEventListener('click', () => {
      this.audio.setEnabled(!this.audio.voiceEnabled);
      byId('soundBtn').textContent = this.audio.voiceEnabled ? 'Voice on' : 'Voice off';
      this.setAudioStatus(this.audio.voiceEnabled ? 'VOICE READY' : 'VOICE MUTED');
    });
    byId('pauseBtn').addEventListener('click', () => this.pause());
    byId('panelToggle')?.addEventListener('click', () => {
      const minimized = document.body.classList.toggle('panel-min');
      byId('panelToggle').textContent = minimized ? 'Show text' : 'Hide text';
    });
    byId('resumeBtn').addEventListener('click', () => this.resume());
    byId('headsetMenu')?.addEventListener('click', () => this.pause());
    byId('leaveViewerBtn')?.addEventListener('click', () => this.leaveViewer());
    byId('exitBtn').addEventListener('click', () => this.exit());
    byId('leaveBtn').addEventListener('click', () => this.exit());
    byId('rewindBtn').addEventListener('click', () => this.restart());
    byId('reviewBtn').addEventListener('click', () => byId('result').classList.remove('open'));
    // A background tab or a locked phone pauses the muted clip; resume it when the player comes back.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible' || !this.started) return;
      const video = byId('performanceVideo');
      if (video?.getAttribute('src') && video.paused) video.play().catch(() => {});
      const spatial = this.engine?.performanceVideo;
      if (spatial?.getAttribute('src') && spatial.paused) spatial.play().catch(() => {});
    });
    byId('leaderboardBtn').addEventListener('click', () => this.showLeaderboard());
    byId('closeLeaderboardBtn').addEventListener('click', () => byId('leaderboard').classList.remove('open'));
    byId('profileToggle').addEventListener('click', () => {
      const form = byId('profileForm');
      form.hidden = !form.hidden;
      byId('profileToggle').textContent = form.hidden ? 'Create an optional player profile' : 'Hide profile form';
    });
    byId('profileCreate').addEventListener('click', () => this.createProfile());
    // Talk to the characters: type, or use the talk button (speech to text in the browser)
    byId('composer')?.addEventListener('submit', event => {
      event.preventDefault();
      this.say(byId('sayInput').value, this.lastInputMethod || 'typed');
    });
    byId('sayInput')?.addEventListener('input', () => { this.lastInputMethod = 'typed'; });
    this.setupMic();
  }

  setupMic() {
    const button = byId('micBtn');
    if (!button) return;
    const canRecord = Boolean(navigator.mediaDevices?.getUserMedia && window.MediaRecorder);
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!canRecord && !Recognition) return;
    button.hidden = false;
    button.addEventListener('click', () => {
      if (this.listening) { this.stopListening?.(); return; }
      // speech to text on the server unless the account switched it off (Live scene tab, engine settings)
      const server = canRecord && this.boot?.speech?.server !== false && !this.serverSpeechOff;
      if (server) this.listenRecorded(button);
      else if (Recognition) this.listenBrowser(button, Recognition);
      else byId('sayStatus').textContent = 'Speech is off. You can type instead.';
    });
  }

  // Names and street words in this scene, sent with the recording so the speech service hears them right
  // (session 66: the browser heard "Dre a punk" as "draw a pumpkin").
  speechTerms() {
    const d = this.definition || {}, live = d.live || {};
    const terms = new Set(['punk', 'walk up on him', 'y\'all', 'ain\'t', 'finna', 'beef', 'opp', 'bruh', 'on God', 'for real', 'step to him', 'back up', 'chill', 'trippin']);
    (d.characters || []).forEach(c => c.name && terms.add(c.name));
    (d.extras || []).forEach(c => c.name && terms.add(String(c.name).replace(/^Mr\.? /, '')));
    (live.facts || []).forEach(f => (f.names || []).forEach(n => terms.add(n)));
    (live.keyterms || []).forEach(t => terms.add(t));
    return [...terms].filter(t => t && String(t).length < 50).slice(0, 100);
  }

  // Talk button: record the player, stop by itself after a short pause (or press again), send the clip to the
  // speech service, then say what came back.
  async listenRecorded(button) {
    const status = text => { byId('sayStatus').textContent = text; };
    let stream;
    try {
      stream = this.micStream && this.micStream.active ? this.micStream
        : await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      this.micStream = stream;
    } catch {
      status('Microphone permission was not given. You can type instead.');
      return;
    }
    const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'].find(m => MediaRecorder.isTypeSupported?.(m)) || '';
    const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    const chunks = [];
    recorder.ondataavailable = e => { if (e.data && e.data.size) chunks.push(e.data); };
    // pause detection on the microphone level
    await this.audio.unlock().catch(() => {});
    const ctx = this.audio.context;
    let analyser = null, srcNode = null, timer = 0;
    if (ctx) {
      srcNode = ctx.createMediaStreamSource(stream);
      analyser = ctx.createAnalyser(); analyser.fftSize = 512;
      srcNode.connect(analyser);
    }
    const started = performance.now();
    let heard = false, quietSince = 0;
    const finish = () => {
      clearInterval(timer);
      try { srcNode?.disconnect(); } catch { /* already gone */ }
      if (recorder.state !== 'inactive') recorder.stop();
    };
    this.stopListening = finish;
    this.listening = true;
    button.textContent = 'Listening...';
    button.classList.add('on');
    status('Listening... speak, then pause.');
    if (analyser) {
      const buf = new Uint8Array(analyser.fftSize);
      timer = setInterval(() => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0; for (let i = 0; i < buf.length; i += 1) { const v = (buf[i] - 128) / 128; sum += v * v; }
        const level = Math.sqrt(sum / buf.length);
        const now = performance.now();
        if (level > 0.035) { heard = true; quietSince = 0; } else if (heard && !quietSince) quietSince = now;
        if ((heard && quietSince && now - quietSince > 1100) || now - started > 15000 || (!heard && now - started > 7000)) finish();
      }, 80);
    }
    recorder.onstop = async () => {
      this.listening = false;
      this.stopListening = null;
      button.textContent = 'Talk';
      button.classList.remove('on');
      const seconds = (performance.now() - started) / 1000;
      if (analyser && !heard) { status('Did not hear anything. Try again or type it.'); return; }
      const blob = new Blob(chunks, { type: recorder.mimeType || mime || 'audio/webm' });
      if (blob.size < 1500) { status('Did not catch that. Try again or type it.'); return; }
      status('Got it...');
      try {
        const audio = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        const result = await this.api.post('stt', { audio, mime: blob.type, seconds: Math.round(seconds * 10) / 10, keyterms: this.speechTerms() });
        const text = String(result.text || '').trim();
        if (!text) { status('Did not catch that. Try again or type it.'); return; }
        byId('sayInput').value = text;
        status('');
        this.say(text, 'spoken');
      } catch (error) {
        const msg = String(error.message || '');
        // the speech service refused this site's key (401/403 or a missing permission): switch to the
        // browser's own listener for the rest of the scene so the Talk button keeps working
        if (/401|403|Unauthorized|Forbidden|permission|not set up|switched off/i.test(msg) && (window.SpeechRecognition || window.webkitSpeechRecognition)) {
          this.serverSpeechOff = true;
          console.warn('[peace] server speech off for this scene:', msg);
          status('Clear speech is not available right now. Tap Talk again to use the basic listener, or type it.');
        } else {
          status(msg || 'Speech did not work. You can type instead.');
        }
      }
    };
    recorder.start();
  }

  // Older browsers without recording: the browser's own speech recognition.
  listenBrowser(button, Recognition) {
    const recognition = new Recognition();
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    this.recognition = recognition;
    this.listening = true;
    this.stopListening = () => recognition.stop();
    button.textContent = 'Listening...';
    button.classList.add('on');
    let finalText = '';
    recognition.onresult = event => {
      let text = '';
      for (const result of event.results) text += result[0].transcript;
      byId('sayInput').value = text;
      if (event.results[event.results.length - 1].isFinal) finalText = text;
    };
    const done = () => {
      this.listening = false;
      this.stopListening = null;
      button.textContent = 'Talk';
      button.classList.remove('on');
      const text = finalText || byId('sayInput').value;
      if (text.trim()) this.say(text, 'spoken');
    };
    recognition.onend = done;
    recognition.onerror = event => { byId('sayStatus').textContent = event.error === 'not-allowed' ? 'Microphone permission was not given. You can type instead.' : 'Did not catch that. Try again or type it.'; };
    try { recognition.start(); } catch { done(); }
  }

  // Words the player says or types: matched to an authored choice; the character answers live.
  async say(rawText, inputMethod) {
    if (this.liveMode) return this.liveTurn(rawText, inputMethod);
    const text = String(rawText || '').trim();
    if (text.length < 2 || this.saying || !this.currentChoices.length) return;
    const token = this.nodeToken;
    const nodeKey = this.nodeKey;
    this.saying = true;
    this.setComposer(false, 'Thinking...');
    byId('feedback').textContent = `You: ${text}`;
    byId('feedback').className = 'feedback';
    this.engine.setCaption(`You: ${text}`);
    let result;
    try {
      result = await this.api.respond({ nodeKey, response: text, inputMethod });
    } catch (error) {
      this.saying = false;
      if (token === this.nodeToken) this.setComposer(true, error.message);
      return;
    }
    if (token !== this.nodeToken) { this.saying = false; return; }
    byId('sayInput').value = '';
    const reply = result.reply || '';
    if (reply) {
      const speakerKey = result.speaker || '';
      const character = (this.definition.characters || []).find(item => item.key === speakerKey);
      byId('speaker').textContent = character?.name || speakerKey;
      byId('dialogueText').textContent = reply;
      this.engine.setCaption(`${character?.name || ''}: ${reply}`);
      if (result.audiourl && this.audio.voiceEnabled) {
        this.engine.setSpeaking(speakerKey);
        const started = await this.audio.play(result.audiourl);
        if (started) {
          await new Promise(resolve => {
            this.audio.element.addEventListener('ended', resolve, { once: true });
            this.audio.element.addEventListener('error', resolve, { once: true });
          });
        }
        this.engine.setSpeaking('');
      } else {
        await new Promise(resolve => setTimeout(resolve, Math.max(1800, reply.split(/\s+/).length * 380)));
      }
    }
    this.saying = false;
    if (token !== this.nodeToken) return;
    const choice = result.matched ? this.currentChoices.find(item => item.key === result.choicekey) : null;
    if (choice) {
      await this.choose(choice, { said: text, inputMethod, skipSpoken: true });
      return;
    }
    this.setComposer(true, reply ? 'Say more, try another way, or pick a choice.' : 'That did not land here. Try another way, or pick a choice.');
  }

  // ---------- live scene ----------
  showTestBadge() {
    if (byId('testBadge')) return;
    const badge = document.createElement('div');
    badge.id = 'testBadge';
    badge.className = 'test-badge';
    badge.textContent = 'TEST DRAFT · not seen by players · turns are marked as test';
    document.body.appendChild(badge);
  }

  // The player's turn is open: talk box, and after a pause, up to three suggested lines.
  openLiveTurn(suggestions = []) {
    this.liveSuggestions = suggestions;
    byId('decisionPrompt').textContent = 'Your move. Say or do something.';
    byId('choices').innerHTML = '';
    this.showComposer(true);
    this.renderFlag();
    clearTimeout(this.suggestTimer);
    const show = () => this.renderSuggestions(this.liveSuggestions);
    if (this.sessionMode === ViewModes.HEADSET || this.sessionMode === ViewModes.XR) show();
    // suggestions come right after the reply (the owner found the old 6 s hold too slow, 2026-10-14); typing stays open
    else this.suggestTimer = setTimeout(show, 800);
  }

  renderSuggestions(list) {
    const holder = byId('choices');
    holder.innerHTML = '';
    const items = (list || []).filter(Boolean).slice(0, 3);
    items.forEach((text, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'choice suggestion';
      button.style.setProperty('--choice-delay', `${index * 80}ms`);
      button.innerHTML = `<b>${index + 1}</b><span><strong>${safe(text)}</strong><small>Suggested line</small></span>`;
      button.addEventListener('click', () => this.liveTurn(text, 'suggestion'));
      holder.appendChild(button);
    });
    this.engine.setChoices(items.map(text => ({ key: 'live', live: true, label: text })));
  }

  renderFlag() {
    if (!this.isTest || !this.lastTurnId) { byId('flagTurn')?.remove(); return; }
    let box = byId('flagTurn');
    if (!box) {
      box = document.createElement('form');
      box.id = 'flagTurn';
      box.className = 'flag-turn';
      box.innerHTML = '<input id="flagNote" maxlength="1000" placeholder="Something off in the last turn? Note it here"><button type="submit">Flag last turn</button><span id="flagState"></span>';
      byId('composer').after(box);
      box.addEventListener('submit', async event => {
        event.preventDefault();
        try {
          await this.api.flag({ turnId: this.flagTurnId, note: byId('flagNote').value });
          byId('flagState').textContent = 'Flagged.';
          byId('flagNote').value = '';
        } catch (error) { byId('flagState').textContent = error.message; }
      });
    }
    this.flagTurnId = this.lastTurnId;
    byId('flagState').textContent = '';
  }

  // One turn: send what the player said, then perform the reply the server decided.
  async liveTurn(rawText, inputMethod) {
    const text = String(rawText || '').trim();
    if (!text || this.saying || !this.started) return;
    clearTimeout(this.suggestTimer);
    this.saying = true;
    this.engine.setChoices([]);
    byId('choices').innerHTML = '';
    this.setComposer(false, 'Thinking...');
    byId('feedback').textContent = `You: ${text}`;
    byId('feedback').className = 'feedback';
    this.engine.setCaption(`You: ${text}`);
    let result;
    try {
      result = await this.api.turn({ said: text, inputMethod });
    } catch (error) {
      this.saying = false;
      this.setComposer(true, error.message);
      this.renderSuggestions(this.liveSuggestions);
      return;
    }
    byId('sayInput').value = '';
    this.lastTurnId = Number(result.turnid || 0);
    await this.performEvents(result);
    // the threat level and tags the server decided: the crowd reacts by type (films, backs off, takes cover, leaves)
    this.engine.staged?.setThreat?.(result.threat, result.tags);
    await this.performLines(result);
    this.pressure = Math.max(0, Math.min(100, Number(result.pressure ?? this.pressure)));
    this.renderPressure();
    this.saying = false;
    if (result.ending) {
      const node = this.definition.nodes?.[result.endingnode] || {};
      this.showComposer(false);
      await this.complete({ ...node, outcome: node.outcome || result.ending });
      this.renderFlag();
      return;
    }
    this.openLiveTurn(result.suggestions || []);
  }

  // Events this turn that are performed before anyone speaks (event.perform, from the shared library): e.g. a
  // gunshot - the person holding the prop plays the pose's one-shot move, the event's sound plays, people whose
  // held pose is listed in holdBack (someone going down) change only after it; a beat of quiet, then the lines.
  async performEvents(result) {
    const defs = result.eventdefs || {};
    for (const key of (result.events || [])) {
      const def = defs[key] || {};
      const perform = def.perform || null;
      const stage = result.stage || {};
      if (!perform) { if (def.sound) { await this.audio.unlock().catch(() => {}); playSound(def.sound, this.audio.context); } continue; }
      const holdBack = (perform.holdback || []).map(x => String(x).toLowerCase());
      const before = JSON.parse(JSON.stringify(stage.people || {}));
      Object.values(before).forEach(p => { if (holdBack.includes(String(p.do || '').toLowerCase())) p.do = 'idle'; });
      this.engine.stage({ focus: '', staging: { people: before, crowd: stage.crowd || {} } });
      await new Promise(resolve => setTimeout(resolve, Number(perform.leadms ?? 600)));
      this.engine.staged?.performPose?.(perform.by, perform.pose);
      if (def.sound) { await this.audio.unlock().catch(() => {}); playSound(def.sound, this.audio.context); }
      await new Promise(resolve => setTimeout(resolve, 150));
      this.engine.staged?.setThreat?.(result.threat, result.tags);
      this.engine.stage({ focus: '', staging: { people: JSON.parse(JSON.stringify(stage.people || {})), crowd: stage.crowd || {} } });
      await new Promise(resolve => setTimeout(resolve, Number(perform.afterms ?? 1300)));
    }
  }

  // Lines in order; a line marked overlapPrevious starts before the previous one ends (people talking over each other).
  // Positions come from the server's stage; each speaker's action holds for the rest of the turn.
  async performLines(result) {
    const lines = Array.isArray(result.lines) ? result.lines : [];
    const stage = result.stage || {};
    const characters = this.definition.characters || [];
    const nameOf = key => characters.find(item => item.key === key)?.name || key;
    const people = JSON.parse(JSON.stringify(stage.people || {}));
    // what the scene line says follows this turn: the set moment that fired, otherwise nothing stale from the opening
    const momentKey = result.endingnode || result.beatnode || '';
    byId('sceneContext').textContent = momentKey ? (this.definition.nodes?.[momentKey]?.context || '') : '';
    let previous = null;
    let previousAudio = null;
    let channel = 0;
    if (lines[1]?.audiourl) this.voice2.preload(lines[1].audiourl);
    const waits = [];
    for (let index = 0; index < lines.length; index += 1) {
      const line = lines[index];
      if (previous) {
        if (line.overlapprevious) await Promise.race([previous, this.overlapPoint(previousAudio)]);
        else await previous;
      }
      Object.values(people).forEach(person => { if (person.do === 'talk') person.do = 'idle'; });
      const person = people[line.speaker] || (people[line.speaker] = {});
      // a held pose (pose map "hold": a weapon out, someone down) stays while they talk (the server's stage carries it)
      const holding = Boolean(this.definition.engine?.poses?.[String(person.do || '').toLowerCase()]?.hold);
      if (!holding) person.do = 'talk';
      (line.actions || []).forEach(action => {
        if (String(action).startsWith('face:')) person.face = String(action).slice(5);
        else if (holding) return;
        else if (action === 'phone_up') person.do = 'phone';
        else if (action === 'point' || action === 'arms_open') person.do = action;
        // any other move the scene's pose map knows (2026-10-15: offer_bag in the pills scene)
        else if (!String(action).startsWith('step_') && action !== 'idle' && this.definition.engine?.poses?.[String(action).toLowerCase()]) person.do = String(action).toLowerCase();
      });
      this.engine.stage({ focus: line.speaker, staging: { people: JSON.parse(JSON.stringify(people)), crowd: stage.crowd || {} } });
      if (index === 0) {
        const walk = this.engine.stagingDelay();
        if (walk > 0) await new Promise(resolve => setTimeout(resolve, walk * 1000));
      }
      byId('speaker').textContent = nameOf(line.speaker);
      byId('dialogueText').textContent = line.text;
      this.engine.setCaption(`${nameOf(line.speaker)}: ${line.text}`);
      previousAudio = channel % 2 === 0 ? this.audio : this.voice2;
      channel += 1;
      previous = this.playLiveLine(previousAudio, line);
      waits.push(previous);
    }
    await Promise.all(waits);
    this.liveVoices = [];
    this.engine.setSpeakingLevels({});
    this.engine.setSpeaking('');
  }

  // When a cut-in line starts: a little past halfway through the line it talks over (timed from when that line
  // actually starts playing, so a slow download does not make the cut-in land on silence).
  overlapPoint(audio) {
    return new Promise(resolve => {
      const element = audio?.element;
      let since = 0;
      const check = () => {
        if (!element) { resolve(); return; }
        if (!since && !element.paused) since = performance.now();
        const length = Number.isFinite(element.duration) ? element.duration : 0;
        if (since && ((length && element.currentTime >= length * 0.55) || performance.now() - since > 2500)) resolve();
        else setTimeout(check, 60);
      };
      setTimeout(check, 120);
    });
  }

  async playLiveLine(audio, line) {
    const voice = { key: line.speaker, audio };
    this.liveVoices.push(voice);
    const url = line.audiourl || '';
    let started = false;
    if (url && this.audio.voiceEnabled) {
      audio.voiceEnabled = true;
      started = await audio.play(url);
    }
    if (started) {
      await new Promise(resolve => {
        audio.element.addEventListener('ended', resolve, { once: true });
        audio.element.addEventListener('error', resolve, { once: true });
      });
    } else {
      await new Promise(resolve => setTimeout(resolve, Math.max(1600, String(line.text || '').split(/\s+/).length * 380)));
    }
    voice.key = '';
  }

  setComposer(enabled, status = '') {
    const form = byId('composer');
    if (!form) return;
    form.querySelectorAll('input,button').forEach(item => { item.disabled = !enabled; });
    byId('sayStatus').textContent = status;
  }

  showComposer(visible) {
    const form = byId('composer');
    if (!form) return;
    // typing needs a screen: computer and "use your phone"; the viewer and VR headset use the choices
    form.hidden = !visible || this.sessionMode === ViewModes.HEADSET || this.sessionMode === ViewModes.XR;
    if (!form.hidden) this.setComposer(true, '');
  }

  updateWordsNotice() {
    const conv = this.definition.conversation || {};
    const keep = conv.storewords !== false;
    const text = keep
      ? 'What you say or type to the characters is saved with this practice so the program team can review and improve it.'
      : 'What you say or type to the characters is used only to run the scene and is not saved.';
    document.querySelectorAll('[data-words-notice]').forEach(item => { item.textContent = text; });
  }

  async createProfile() {
    const status = byId('profileStatus');
    if (!byId('profileConsent').checked) {
      status.textContent = 'Consent is required to create a connected profile.';
      return;
    }
    const button = byId('profileCreate');
    button.disabled = true;
    status.textContent = 'Creating your private profile…';
    try {
      const result = await this.api.register({
        programId: this.boot.program.id,
        firstName: byId('profileFirst').value.trim(),
        lastName: byId('profileLast').value.trim(),
        email: byId('profileEmail').value.trim(),
        displayAlias: byId('profileAlias').value.trim(),
        leaderboardOptIn: byId('leaderboardOptIn').checked,
        consentAccepted: true
      });
      byId('accessCode').value = result.accesscode || '';
      byId('identityState').textContent = 'Profile code ready';
      status.textContent = `Code created: ${result.accesscode}. Save it to reconnect future practice.`;
    } catch (error) {
      status.textContent = error.message;
    } finally {
      button.disabled = false;
    }
  }

  fillPublishedContent() {
    const program = this.boot.program;
    const scenario = this.boot.scenario;
    const briefing = this.definition.briefing || {};
    byId('programName').textContent = program.name;
    byId('scenarioName').textContent = scenario.name;
    byId('scenarioSummary').textContent = scenario.summary;
    this.showVersionLine();
    this.updateWordsNotice();
    byId('briefRole').textContent = briefing.role || 'You are part of the scene. Your actions influence what happens next.';
    byId('briefSituation').textContent = briefing.situation || scenario.summary;
    byId('briefGoal').textContent = briefing.goal || 'Create time, distance, accurate information, or trusted support before pressure takes over.';
    byId('safetyNotice').textContent = program.safetynotice;
    byId('crisisResource').textContent = program.crisisresourcetext;
    byId('locationName').textContent = `${this.definition.location?.name || 'Baton Rouge'} · ${program.city}, ${program.state}`;
    byId('ambientText').textContent = this.definition.location?.ambient || '';
    const cast = byId('briefCast');
    cast.innerHTML = (this.definition.characters || []).map(character => `<li><strong>${safe(character.name)}</strong><span>${safe(String(character.role || 'person in the scene').replaceAll('_', ' '))}</span></li>`).join('');
    document.documentElement.style.setProperty('--brand-primary', program.primarycolor || '#ffd25a');
    document.documentElement.style.setProperty('--brand-accent', program.accentcolor || '#67e0c0');
  }

  reportCastState() {
    const readiness = this.getMediaReadiness();
    const readinessNotice = byId('readinessNotice');
    const modeButtons = [...document.querySelectorAll('[data-mode]')];
    if (readiness.ready) {
      byId('buildState').textContent = this.engine?.stagedMode ? '3D SCENE + NATURAL VOICE READY' : 'REALISTIC MOTION + NATURAL VOICE READY';
      byId('buildState').classList.add('ready');
      if (readinessNotice) readinessNotice.hidden = true;
      modeButtons.forEach(button => { button.disabled = false; });
      return;
    }
    byId('buildState').textContent = 'PRODUCTION MEDIA REQUIRED';
    byId('buildState').classList.add('prototype');
    const missing = [];
    if (!readiness.hasPublishedNodes) missing.push('published scene content');
    if (readiness.missingVideo.length) missing.push(`${readiness.missingVideo.length} motion performances`);
    if (readiness.missingVoice.length) missing.push(`${readiness.missingVoice.length} natural voice files`);
    if (readinessNotice) {
      readinessNotice.textContent = `Gameplay is not ready yet. The facilitator must publish ${missing.join(' and ')} before any play mode can start.`;
      readinessNotice.hidden = false;
    }
    modeButtons.forEach(button => { button.disabled = true; });
  }

  getMediaReadiness() {
    const server = this.boot?.readiness;
    const nodes = Object.entries(this.definition?.nodes || {});
    const serverMissingVideo = server?.missingVideo || server?.missingvideo || server?.MISSINGVIDEO;
    const serverMissingVoice = server?.missingVoice || server?.missingvoice || server?.MISSINGVOICE;
    // Staged 3D scenes play the approved bodies and staging; the video clips are not needed.
    const staged = Boolean(this.engine?.stagedMode);
    const missingVideo = staged ? [] : Array.isArray(serverMissingVideo)
      ? serverMissingVideo
      : nodes.filter(([, node]) => !(node.performancevideo || node.performanceVideo)).map(([key]) => key);
    const missingVoice = Array.isArray(serverMissingVoice)
      ? serverMissingVoice
      : nodes.filter(([, node]) => node.text && !(node.audiourl || node.audioURL || node.audio)).map(([key]) => key);
    const hasPublishedNodes = nodes.length > 0;
    return {
      ready: hasPublishedNodes && (staged ? missingVoice.length === 0 : (server?.ready === true || server?.READY === true || (missingVideo.length === 0 && missingVoice.length === 0))),
      missingVideo,
      missingVoice,
      hasPublishedNodes
    };
  }

  async start(mode) {
    try {
      this.clearNotice();
      const readiness = this.getMediaReadiness();
      if (!readiness.ready) {
        const parts = [];
        if (!readiness.hasPublishedNodes) parts.push('published scenario nodes');
        if (readiness.missingVideo.length) parts.push(`motion: ${readiness.missingVideo.join(', ')}`);
        if (readiness.missingVoice.length) parts.push(`voice: ${readiness.missingVoice.join(', ')}`);
        throw new Error(`This scenario is not published for play. Required realistic character performances are missing (${parts.join('; ')}). An administrator must build and approve the media first.`);
      }
      const requested = Object.values(ViewModes).includes(mode) ? mode : ViewModes.BROWSER;
      this.sessionMode = await this.engine.setMode(requested);
      this.voice2.unlock().catch(() => false);
      await Promise.race([
        this.audio.unlock().catch(() => false),
        new Promise(resolve => setTimeout(() => resolve(false), 900))
      ]);
      const deviceMode = this.sessionMode === ViewModes.XR ? 'vr' : (this.sessionMode === ViewModes.BROWSER ? (innerWidth < 760 ? 'mobile' : 'browser') : 'mobile');
      const response = await this.api.start({
        programId: this.boot.program.id,
        scenarioId: this.boot.scenario.id,
        versionId: this.boot.scenario.versionid,
        deviceMode,
        cohortCode: byId('cohortCode').value.trim(),
        accessCode: byId('accessCode').value.trim(),
        as: this.versionKey
      });
      this.started = true;
      this.isTest = Boolean(response.test);
      document.body.dataset.viewMode = this.sessionMode;
      byId('briefing').classList.remove('open');
      byId('recenterBtn').hidden = this.sessionMode === ViewModes.BROWSER;
      if (this.sessionMode !== ViewModes.BROWSER) this.engine.recenter();
      if (response.identified) byId('identityState').textContent = 'Progress connected';
      await this.enter(this.definition.openingnode || this.definition.openingNode || Object.keys(this.definition.nodes)[0]);
      if (this.sessionMode === ViewModes.XR) await this.log({ eventType: 'vr_entered', nodeKey: this.nodeKey });
    } catch (error) {
      this.notice(error.message, true);
    }
  }

  async enter(key) {
    const node = this.definition.nodes?.[key];
    if (!node) {
      this.notice(`Published node “${key}” is missing.`, true);
      return;
    }
    const token = ++this.nodeToken;
    this.nodeKey = key;
    this.currentChoices = node.choices || [];
    this.pressure = Math.max(0, Math.min(100, Number(node.pressure ?? this.pressure)));
    this.renderPressure();
    const mediaReady = await this.renderNodeMedia(node, token);
    if (!mediaReady || token !== this.nodeToken) return;
    this.engine.stage(node);
    this.engine.setChoices([]);
    const walk = this.engine.stagingDelay();
    if (walk > 0) {
      await new Promise(resolve => setTimeout(resolve, walk * 1000));
      if (token !== this.nodeToken) return;
    }
    byId('sceneContext').textContent = node.context || '';
    const character = (this.definition.characters || []).find(item => item.key === node.focus);
    byId('speaker').textContent = `${node.speaker || 'Scene'}${character?.role ? ` · ${String(character.role).replaceAll('_', ' ')}` : ''}`;
    byId('dialogueText').textContent = node.text || '';
    this.engine.setCaption(node.text ? `${node.speaker ? `${node.speaker}: ` : ''}${node.text}` : '');
    byId('decisionPrompt').textContent = node.prompt || node.reflection || '';
    byId('feedback').textContent = '';
    byId('feedback').className = 'feedback';
    byId('choices').innerHTML = '<div class="performance-wait"><span></span>Watch and listen. Your moment to act is coming.</div>';
    this.showComposer(false);
    await this.log({ eventType: 'node_entered', nodeKey: key });
    this.engine.setSpeaking(node.focus || '');
    const played = await this.playNodeVoice(node, token);
    this.engine.setSpeaking('');
    if (token !== this.nodeToken) return;
    if (!played) {
      this.stopNodeMedia();
      return;
    }
    if (node.outcome) {
      await this.complete(node);
      return;
    }
    if (token !== this.nodeToken) return;
    if (this.liveMode) {
      this.openLiveTurn((node.choices || []).map(choice => choice.spoken || choice.label).filter(Boolean).slice(0, 3));
      return;
    }
    this.renderChoices(this.currentChoices);
  }

  async renderNodeMedia(node, nodeToken) {
    const layer = byId('cinematic');
    const video = byId('performanceVideo');
    const videoUrl = node.performancevideo || node.performanceVideo || '';
    this.stopNodeMedia();
    if (this.engine.stagedMode) return true; // people are performed live in the 3D scene
    const mediaToken = ++this.mediaToken;
    if (!videoUrl) {
      this.notice(`Scene “${this.nodeKey}” cannot play because its approved realistic character-motion performance is missing.`, true);
      return false;
    }
    this.engine.setPrototypeVisibility(false);
    if (this.sessionMode !== ViewModes.BROWSER) {
      try {
        const ready = await this.engine.playPerformanceVideo(videoUrl);
        if (!ready || mediaToken !== this.mediaToken || nodeToken !== this.nodeToken) return false;
        return true;
      } catch (error) {
        this.notice(`Scene “${this.nodeKey}” motion failed to load: ${error.message}`, true);
        return false;
      }
    }
    layer.classList.add('active');
    video.pause();
    video.removeAttribute('src');
    video.load();
    video.hidden = false;
    video.src = videoUrl;
    this.applyPerformanceFraming(video, node);
    const loaded = await new Promise(resolve => {
      let settled = false;
      const finish = value => {
        if (settled) return;
        settled = true;
        resolve(value);
      };
      video.addEventListener('loadeddata', () => finish(true), { once: true });
      video.addEventListener('error', () => finish(false), { once: true });
      setTimeout(() => finish(false), 10000);
    });
    if (!loaded || mediaToken !== this.mediaToken || nodeToken !== this.nodeToken) {
      this.notice(`Scene “${this.nodeKey}” motion media could not be loaded.`, true);
      this.stopNodeMedia();
      return false;
    }
    try {
      await video.play();
      return true;
    } catch (error) {
      this.notice(`Scene “${this.nodeKey}” motion could not start: ${error.message}`, true);
      this.stopNodeMedia();
      return false;
    }
  }

  applyPerformanceFraming(video, node) {
    // Portrait phones crop a 16:9 clip to its middle; aim the crop at the speaking character.
    const focusX = Number(node?.framing?.x);
    video.style.objectPosition = Number.isFinite(focusX) ? `${Math.max(0, Math.min(100, focusX))}% 44%` : '';
    // Once the clip has played through, keep its final seconds cycling instead of freezing on a still frame.
    video.loop = false;
    video.onended = () => {
      if (!video.getAttribute('src')) return;
      const length = video.duration;
      if (!Number.isFinite(length) || length <= 0) return;
      video.currentTime = Math.max(0, length - 2.5);
      video.play().catch(() => {});
    };
  }

  stopNodeMedia() {
    ++this.mediaToken;
    const layer = byId('cinematic');
    const video = byId('performanceVideo');
    layer?.classList.remove('active');
    if (video) {
      video.onended = null;
      video.pause();
      video.removeAttribute('src');
      video.load();
      video.hidden = true;
    }
    this.engine?.stopPerformanceVideo();
  }

  async playNodeVoice(node, token) {
    if (!node.text) return true;
    if (!this.audio.voiceEnabled) return true;
    let url = node.audiourl || node.audioURL || node.audio || this.voiceCache.get(this.nodeKey) || '';
    if (!url) {
      try {
        this.setAudioStatus('PREPARING NATURAL VOICE');
        const result = await this.api.voice({ nodeKey: this.nodeKey });
        url = result.audiourl || '';
        if (url) this.voiceCache.set(this.nodeKey, url);
      } catch (error) {
        this.setAudioStatus('NATURAL VOICE FAILED');
        this.notice(`Scene “${this.nodeKey}” stopped because its required natural voice failed: ${error.message}`, true);
        return false;
      }
    }
    const started = await this.audio.play(url);
    if (!started) {
      this.notice(`Scene “${this.nodeKey}” stopped because its required natural voice could not start. Tap Start again after audio permission is available.`, true);
      return false;
    }
    await new Promise(resolve => {
      const finish = () => resolve();
      this.audio.element.addEventListener('ended', finish, { once: true });
      this.audio.element.addEventListener('error', finish, { once: true });
      setTimeout(() => {
        if (token === this.nodeToken && this.audio.element.paused) finish();
      }, 750);
    });
    return true;
  }

  renderChoices(choices) {
    const holder = byId('choices');
    holder.innerHTML = '';
    this.showComposer(choices.length > 0);
    choices.forEach((choice, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'choice';
      button.style.setProperty('--choice-delay', `${index * 80}ms`);
      button.innerHTML = `<b>${index + 1}</b><span><strong>${safe(choice.label)}</strong><small>${safe(choice.cue || '')}</small></span>`;
      button.addEventListener('click', () => this.choose(choice));
      holder.appendChild(button);
    });
    this.engine.setChoices(choices);
  }

  async choose(choice, options = {}) {
    if (!choice) return;
    if (this.liveMode && choice.live) { await this.liveTurn(choice.label, 'suggestion'); return; }
    this.showComposer(false);
    if (choice.key === '__restart') {
      this.engine.setChoices([]);
      this.engine.setCaption('');
      await this.restart();
      return;
    }
    ++this.nodeToken;
    this.stopNodeMedia();
    this.audio.stop();
    this.engine.setChoices([]);
    byId('choices').innerHTML = '';
    this.pressure = Math.max(0, Math.min(100, this.pressure + Number(choice.delta || 0)));
    this.renderPressure();
    if (choice.skill) this.skillHistory.add(choice.skill);
    const said = options.said || choice.spoken || choice.feedback || choice.label;
    byId('feedback').textContent = options.said ? `You: ${options.said}` : said;
    // In the 3D scene the player's own words come first, so the reply that follows makes sense.
    if (this.engine.stagedMode && !options.skipSpoken) this.engine.setCaption(choice.spoken ? `You: ${choice.spoken}` : '');
    byId('feedback').className = Number(choice.delta || 0) <= 0 ? 'feedback skill' : 'feedback risk';
    await this.log({
      eventType: 'choice_selected',
      nodeKey: this.nodeKey,
      choiceKey: choice.key,
      escalationDelta: Number(choice.delta || 0),
      skillCode: choice.skill || '',
      meta: { playerAction: choice.spoken || choice.label, inputMethod: options.inputMethod || 'tap' }
    });
    const readTime = options.skipSpoken ? 300 : (this.engine.stagedMode && choice.spoken ? Math.max(1.8, String(choice.spoken).split(/\s+/).length / 2.8) * 1000 : 900);
    await new Promise(resolve => setTimeout(resolve, readTime));
    await this.enter(choice.next);
  }

  async complete(node) {
    let gamification = {};
    if (!this.isTest) try {
      const result = await this.api.event({ eventType: 'completed', nodeKey: this.nodeKey, outcomeKey: node.outcome, meta: { pressure: this.pressure } });
      gamification = result.gamification || {};
    } catch (error) {
      console.warn('Completion scoring was not available:', error);
    }
    const safeOutcome = node.outcome === 'deescalated';
    const score = gamification.competencyscore ?? Math.max(0, Math.round(100 - this.pressure * 0.68 + this.skillHistory.size * 7));
    byId('resultScore').textContent = String(Math.round(Number(score)));
    byId('resultOutcome').textContent = node.closing || (safeOutcome ? 'You created a believable off-ramp.' : 'The scene reached the point where conversation was no longer dependable.');
    const skills = gamification.skills || [...this.skillHistory];
    byId('resultSkills').innerHTML = skills.map(skill => `<span>${safe(String(skill).replaceAll('_', ' '))}</span>`).join('') || '<span>pressure awareness</span>';
    byId('resultReflection').textContent = node.reflection || '';
    byId('result').classList.add('open');
    // VR / phone headset can't see the page: show the outcome and a replay board in the scene.
    if (this.sessionMode === ViewModes.XR || this.sessionMode === ViewModes.HEADSET) {
      this.engine.setCaption(byId('resultOutcome').textContent);
      this.engine.setChoices([{ key: '__restart', label: 'Practice this again' }]);
    }
  }

  async restart() {
    byId('result').classList.remove('open');
    this.pressure = 0;
    this.lastTurnId = 0;
    this.renderFlag();
    this.skillHistory.clear();
    this.engine.staged?.resetScene?.();
    const deviceMode = this.sessionMode === ViewModes.XR ? 'vr' : (innerWidth < 760 ? 'mobile' : 'browser');
    await this.api.start({
      programId: this.boot.program.id,
      scenarioId: this.boot.scenario.id,
      versionId: this.boot.scenario.versionid,
      deviceMode,
      cohortCode: byId('cohortCode').value.trim(),
      accessCode: byId('accessCode').value.trim(),
      as: this.versionKey
    });
    await this.enter(this.definition.openingnode || this.definition.openingNode || Object.keys(this.definition.nodes)[0]);
  }

  pause() {
    this.audio.element.pause();
    byId('pause').classList.add('open');
    const leaveViewer = byId('leaveViewerBtn');
    if (leaveViewer) leaveViewer.hidden = this.sessionMode !== ViewModes.HEADSET;
    this.log({ eventType: 'paused', nodeKey: this.nodeKey });
  }

  // From the phone-headset pause menu: drop the split screen and keep playing in "move phone to look".
  async leaveViewer() {
    this.sessionMode = await this.engine.setMode(ViewModes.SPATIAL);
    this.engine.clearChoiceBoards();
    document.body.dataset.viewMode = this.sessionMode;
    byId('recenterBtn').hidden = this.sessionMode === ViewModes.BROWSER;
    byId('leaveViewerBtn').hidden = true;
    this.engine.recenter();
    this.resume();
  }

  resume() {
    byId('pause').classList.remove('open');
    if (this.audio.voiceEnabled && this.audio.element.src) this.audio.element.play().catch(() => this.setAudioStatus('TAP AUDIO TO PLAY'));
    this.log({ eventType: 'resumed', nodeKey: this.nodeKey });
  }

  exit() {
    this.log({ eventType: 'exited', nodeKey: this.nodeKey });
    location.reload();
  }

  async showLeaderboard() {
    try {
      const data = await this.api.leaderboard();
      const community = data.community || {};
      byId('communityTotals').innerHTML = `<span>${community.practices || 0} practices</span><span>${community.safeoutcomes || 0} off-ramps found</span><span>${community.players || 0} connected players</span>`;
      byId('leaderList').innerHTML = (data.leaders || []).map((entry, index) => `<article><b>${index + 1}</b><strong>${safe(entry.alias)}</strong><span>${Math.round(entry.score)} skill score · ${entry.skills} skills</span></article>`).join('') || '<p>No players have opted into the alias board yet.</p>';
      byId('leaderboard').classList.add('open');
    } catch (error) {
      this.notice(error.message);
    }
  }

  async log(payload) {
    if (!this.started || this.isTest) return;
    try {
      await this.api.event(payload);
    } catch (error) {
      console.warn('Event was not recorded:', error);
    }
  }

  renderPressure() {
    byId('pressureValue').textContent = String(Math.round(this.pressure));
    byId('pressureBar').style.width = `${this.pressure}%`;
    document.body.classList.toggle('high-pressure', this.pressure >= 75);
  }

  setAudioStatus(label) {
    byId('audioState').textContent = label;
  }

  // The version choice (scene "versions"): shown on the loading screen before the scene loads, so the intro, the
  // briefing and every turn are the chosen version. Resolves with the version key; the address keeps it (?as=).
  chooseVersion(versions, scenario) {
    return new Promise(resolve => {
      const host = byId('loading');
      host.classList.add('choosing');
      const card = document.createElement('div');
      card.className = 'version-choice';
      card.id = 'versionChoice';
      const small = document.createElement('small');
      small.textContent = 'THE SCENE';
      const title = document.createElement('h2');
      title.textContent = scenario.name || '';
      const ask = document.createElement('p');
      ask.textContent = 'How do you want to play it?';
      card.append(small, title, ask);
      versions.forEach((v, i) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = i === 0 ? 'version-option first' : 'version-option';
        const label = document.createElement('strong');
        label.textContent = v.label || v.key;
        const blurb = document.createElement('span');
        blurb.textContent = v.blurb || '';
        button.append(label, blurb);
        button.addEventListener('click', () => {
          card.remove();
          host.classList.remove('choosing');
          this.rememberVersion(v.key);
          resolve(String(v.key || ''));
        });
        card.append(button);
      });
      host.append(card);
      setTimeout(() => card.querySelector('button')?.focus(), 50);
    });
  }

  // keep the chosen version in the address, so a reload or a shared link plays the same version
  rememberVersion(key) {
    try {
      const url = new URL(location.href);
      url.searchParams.set('as', key);
      window.history.replaceState(null, '', url.toString());
    } catch { /* the address stays as it was */ }
  }

  // On the briefing: which version this is, and a link to play it the other way (reloads as that version)
  showVersionLine() {
    const versions = Array.isArray(this.definition?.versions) ? this.definition.versions : [];
    byId('versionLine')?.remove();
    if (versions.length < 2) return;
    const current = versions.find(v => v.key === this.versionKey) || versions[0];
    const line = document.createElement('p');
    line.id = 'versionLine';
    line.className = 'version-line';
    const now = document.createElement('span');
    now.textContent = `You're playing: ${current.label || current.key}`;
    line.append(now);
    versions.filter(v => v.key !== current.key).forEach(v => {
      const link = document.createElement('a');
      const url = new URL(location.href);
      url.searchParams.set('as', v.key);
      link.href = url.toString();
      link.textContent = `Play it as ${v.label || v.key}`;
      line.append(link);
    });
    byId('scenarioSummary')?.after(line);
  }

  setLoading(label) {
    byId('loadingText').textContent = label;
  }

  notice(message, critical = false) {
    const notice = byId('notice');
    notice.textContent = message;
    notice.classList.toggle('critical', critical);
    notice.classList.add('show');
  }

  clearNotice() {
    byId('notice').classList.remove('show', 'critical');
  }

  assetError(type, url) {
    console.warn(`Asset ${type} failed:`, url);
  }
}
